import { useEffect, useState, useRef } from "react";
import { twClassNames } from "@scripts/react/helpers/twClassNames";

type WallOfLoveCardInfo = {
    name: string;
    title: string;
    image: string;
    quote: string;
    icon: string;
    url: string;
};

const wallOfLoveCards: WallOfLoveCardInfo[] = [
    {
        name: "AI Daily Brief",
        title: "@Krishna13424681",
        image: "https://pbs.twimg.com/profile_images/1985915389767749634/fje2ERyf_400x400.jpg",
        quote: "SAM3 Excels, But Edge Needs Light/Fast Models - Roboflow Rapid Delivers\nSAM3 is powerful but too large/slow for edge/real-time use. Distill it into smaller models with @roboflow Rapid for 1-second deployment!",
        icon: "fab fa-x-twitter",
        url: "https://x.com/Krishna13424681/status/1992072173788537261"
    },
    {
        name: "Marco Franzon",
        title: "Software Engineer",
        image: "https://media.licdn.com/dms/image/v2/D4D03AQF5HTP-7GU1Gw/profile-displayphoto-shrink_200_200/B4DZOJsfTrHgAc-/0/1733181962730?e=2147483647&v=beta&t=6hQpMA5_2oGKbmmFAsD-tU5wnXmVuI2veoiHqN3UUwM",
        quote: "Roboflow Rapid has completely changed the game for computer vision.\n\nIn under 60 seconds, I went from a raw, unlabeled video into a fully trained, deployed, and usable custom object detection model.",
        icon: "fab fa-linkedin",
        url: "https://www.linkedin.com/feed/update/urn:li:activity:7404210856453914624/"
    },
    {
        name: "varchasvi",
        title: "@varchasvee_",
        image: "https://pbs.twimg.com/profile_images/1990633831339339777/xE7W6iMo_400x400.jpg",
        quote: "Tried Roboflow Rapid and holy wow it actually blew my mind. You just drop in a prompt, upload your video(this video along with many others are available on the same roboflow page), pick a segmentation model (I used SAM-3) and it straight up builds the whole thing for you. It trains, sets everything up for you, and gives you an API you can start using instantly!\nThese models are basically full-on startups if you use them right and it’s wild how far you can get with almost zero effort now.",
        icon: "fab fa-x-twitter",
        url: "https://x.com/varchasvee_/status/1999385018372690104"
    },
    {
        name: "Nick M",
        title: "@n_matamoros",
        image: "https://pbs.twimg.com/profile_images/1658176668949794816/nvdRcJ9H_400x400.jpg",
        quote: "Data strategy makes custom cvis models good. But unless you need low-latency, @meta's SAM 3 on @roboflow Rapid is unbeatable 🧵",
        icon: "fab fa-x-twitter",
        url: "https://x.com/n_matamoros/status/2000757812494778684"
    },
    {
        name: "David",
        title: "@3DXRGuy",
        image: "https://pbs.twimg.com/profile_images/1973126655045476353/D-bBQiXO_400x400.jpg",
        quote: 'trained a computer vision model to track cars and trucks for \n@VIVERSEofficial\n\n- 3 mins to train, typed "cars" and "trucks" in @roboflow\n- 3D visualization rendered in @threejs\n- inspired by @measure_plan\n\nwill upload demo link in replies if ppl want it',
        icon: "fab fa-x-twitter",
        url: "https://x.com/3DXRGuy/status/1999569797781004707"
    }
];

function WallOfLoveCard({
    cardInfo,
    isSpotlighted
}: {
    cardInfo: WallOfLoveCardInfo;
    isSpotlighted: boolean;
}) {
    return (
        <div
            className={twClassNames(
                "flex w-full flex-col gap-4 rounded-xl p-6 ring-1 ring-black/5 transition-all duration-500",
                isSpotlighted ? "bg-white shadow-lg" : "bg-gray-50 shadow-sm"
            )}
        >
            <div className="flex items-center gap-3">
                <img
                    src={cardInfo.image}
                    alt={cardInfo.name}
                    className="h-10 w-10 rounded-full shadow-md ring-1 ring-black/5 transition-all duration-500"
                    style={{ filter: isSpotlighted ? "grayscale(0%)" : "grayscale(100%)" }}
                />
                <div className="flex flex-1 flex-col">
                    <h3
                        className={twClassNames(
                            "text-sm font-medium transition-colors duration-500",
                            isSpotlighted ? "text-secondary" : "text-gray-400"
                        )}
                    >
                        {cardInfo.name}
                    </h3>
                    <p
                        className={twClassNames(
                            "text-sm transition-colors duration-500",
                            isSpotlighted ? "text-tertiary" : "text-gray-400"
                        )}
                    >
                        {cardInfo.title}
                    </p>
                </div>
                <i
                    className={twClassNames(
                        cardInfo.icon,
                        "fa-fw text-lg transition-colors duration-500",
                        isSpotlighted ? "text-gray-400" : "text-gray-300"
                    )}
                ></i>
            </div>
            <p
                className={twClassNames(
                    "whitespace-pre-line text-sm transition-colors duration-500",
                    isSpotlighted ? "text-primary" : "text-gray-400"
                )}
            >
                {cardInfo.quote}
            </p>
        </div>
    );
}

export function WallOfLove() {
    const [spotlightIndex, setSpotlightIndex] = useState(0);
    const [isHovered, setIsHovered] = useState(false);
    const [offset, setOffset] = useState<number | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const cardRefs = useRef<(HTMLDivElement | null)[]>([]);

    const GAP = 32; // gap-8 = 32px
    const cardCount = wallOfLoveCards.length;

    // Triple the cards array for infinite scroll effect
    const extendedCards = [...wallOfLoveCards, ...wallOfLoveCards, ...wallOfLoveCards];
    const actualSpotlightIndex = cardCount + spotlightIndex; // Spotlight in middle set

    // Calculate offset to center the spotlight card
    useEffect(() => {
        const spotlightCard = cardRefs.current[actualSpotlightIndex];
        if (!spotlightCard) return;

        // Calculate offset: sum of heights of cards above + their gaps + half of spotlight card
        let heightAbove = 0;
        for (let i = 0; i < actualSpotlightIndex; i++) {
            const card = cardRefs.current[i];
            if (card) {
                heightAbove += card.offsetHeight + GAP;
            }
        }
        const spotlightHalfHeight = spotlightCard.offsetHeight / 2;
        setOffset(heightAbove + spotlightHalfHeight);
    }, [actualSpotlightIndex]);

    // Auto-advance every 5 seconds - cycle through cards
    useEffect(() => {
        if (isHovered) return;

        const interval = setInterval(() => {
            setSpotlightIndex((prev) => (prev + 1) % cardCount);
        }, 5000);

        return () => clearInterval(interval);
    }, [isHovered, cardCount]);

    const isScrolling = useRef(false);
    const SCROLL_COOLDOWN = 1000; // ms to wait before allowing next scroll

    const handleWheel = (e: React.WheelEvent) => {
        e.preventDefault();
        if (isScrolling.current) return;

        isScrolling.current = true;

        if (e.deltaY > 0) {
            setSpotlightIndex((prev) => (prev + 1) % cardCount);
        } else if (e.deltaY < 0) {
            setSpotlightIndex((prev) => (prev - 1 + cardCount) % cardCount);
        }

        setTimeout(() => {
            isScrolling.current = false;
        }, SCROLL_COOLDOWN);
    };

    return (
        <div
            className="relative hidden h-full w-full max-w-xl overflow-hidden bg-gray-100 lg:flex"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            onWheel={handleWheel}
        >
            <div className="pointer-events-none absolute inset-x-0 top-0 z-10 h-24 bg-gradient-to-b from-gray-100 to-transparent" />
            <div className="pointer-events-none absolute inset-x-0 bottom-0 z-10 h-24 bg-gradient-to-t from-gray-100 to-transparent" />

            <div
                ref={containerRef}
                className="absolute inset-x-8 flex flex-col gap-8 transition-all duration-500 lg:inset-x-12"
                style={{
                    top: offset !== null ? `calc(50% - ${offset}px)` : undefined,
                    opacity: offset !== null ? 1 : 0
                }}
            >
                {extendedCards.map((card, index) => (
                    <div
                        key={`${card.url}-${index}`}
                        ref={(el) => {
                            cardRefs.current[index] = el;
                        }}
                    >
                        <WallOfLoveCard
                            cardInfo={card}
                            isSpotlighted={index === actualSpotlightIndex}
                        />
                    </div>
                ))}
            </div>
        </div>
    );
}
